import { ItemFile, PrintJob } from "@/types.d";
import { convertFromPoints, convertToPoints } from "@/utils/units.utils";

export function getPrintJobFile(job: PrintJob): ItemFile | undefined {
  return job.file ?? job.orderItem?.currentFile;
}

export function sortPrintJobsByPosition(jobs: PrintJob[]): PrintJob[] {
  return [...jobs].sort((a, b) => {
    if (a.position == null && b.position != null) return 1;
    if (a.position != null && b.position == null) return -1;
    if (a.position != null && b.position != null && a.position !== b.position) return a.position < b.position ? -1 : 1;
    const createdAtDifference = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return createdAtDifference || Number(a.id) - Number(b.id);
  });
}

export function preparePrintJobCsvRows(jobs: PrintJob[]): PrintJob[] {
  return sortPrintJobsByPosition(jobs).map(job => {
    const file = getPrintJobFile(job);
    const currentFile = (job as any).currentFile ?? job.orderItem?.currentFile ?? {};
    return {
      ...job,
      currentFile: {
        ...currentFile,
        ...(file?.originalName ? { originalName: file.originalName } : {}),
        metadata: {
          ...(currentFile.metadata ?? {}),
          ImageInfo: currentFile.metadata?.ImageInfo ?? {},
        },
      },
    } as PrintJob;
  });
}

export function formatPrintJobCsvExportValue(data: unknown, field: string, unitCode: string): string {
  if (field === "currentFile.metadata.ImageInfo") {
    const imageInfo = data as { width?: number; height?: number; ResolutionX?: number; ResolutionY?: number } | null;
    const width = Number(imageInfo?.width);
    const height = Number(imageInfo?.height);
    const resolutionX = Number(imageInfo?.ResolutionX);
    const resolutionY = Number(imageInfo?.ResolutionY);
    if (!width || !height || !resolutionX || !resolutionY) return "-";
    try {
      const widthInUnit = convertFromPoints((width / resolutionX) * 72, unitCode);
      const heightInUnit = convertFromPoints((height / resolutionY) * 72, unitCode);
      return widthInUnit == null || heightInUnit == null ? "-" : `${widthInUnit.toFixed(2)} x ${heightInUnit.toFixed(2)} ${unitCode}`;
    } catch {
      return "-";
    }
  }

  const value = data == null ? "" : typeof data === "object" ? JSON.stringify(data) : String(data);
  return value.replace(/"/g, '""');
}

/** Preview file ids used by ItemThumbnail for a print job list. */
export function collectPrintJobPreviewIds(jobs: PrintJob[] | undefined | null): number[] {
  if (!jobs?.length) return [];
  const ids = new Set<number>();
  for (const job of jobs) {
    const file = getPrintJobFile(job);
    const previewId = file?.preview?.id || file?.otherPreviews?.[0]?.id;
    if (typeof previewId === "number" && previewId > 0) ids.add(previewId);
  }
  return [...ids];
}

export function getPrintJobLengthInPoints(job: PrintJob): number {
  if (job.lengthInPt != null) return Number(job.lengthInPt) || 0;
  if (!job.length || !job.lengthUnits || job.lengthUnits === "rep") return 0;
  return convertToPoints(job.length, job.lengthUnits) ?? 0;
}

export function getPrintJobsLengthInUnit(jobs: PrintJob[], unitCode = "m"): { length: string; unit: string } {
  const defaultUnitCode = unitCode || "m";
  const totalPts = jobs.reduce((acc, job) => {
    const jobLength = getPrintJobLengthInPoints(job);
    return acc + (isNaN(jobLength) ? 0 : jobLength);
  }, 0);
  return {
    length: (convertFromPoints(totalPts, defaultUnitCode) || 0).toFixed(2),
    unit: defaultUnitCode,
  };
}
